import React from 'react';
import { useParams } from 'react-router-dom';
import { CheckIcon, MapPinIcon, TrainFrontIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { DestinationHero } from '../../components/destination/DestinationHero';
import { Section } from '../../components/ui/Section';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { CourseCard } from '../../components/ui/CourseCard';
import { MockupTag } from '../../components/ui/MockupTag';
import { TimetableTable } from '../../components/ui/TimetableTable';
import { FaqList } from '../../components/ui/FaqList';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { NotFound } from '../NotFound';
import { destinations } from '../../data/destinations';
import { priceLists } from '../../data/priceLists';
import { courseGroups, courses } from '../../data/courses';
import { accommodationFacilities, accommodationPrices, roomTypes } from '../../data/pageContent';
export function YearRoundDestination() {
  const {
    slug
  } = useParams();
  const destination = destinations.find(item => item.slug === slug);
  const copy = destination?.yearRound;
  usePageMeta(copy?.metaTitle ?? 'Location not found | ES World', copy?.metaDescription ?? 'This page does not exist.');
  if (!destination || !copy) return <NotFound data-id="element-1141" data-mp="kpkiygwzqfzm" />;
  const available = courses.filter(course => course.season === 'year-round' && course.locations.includes(destination.slug));
  const priceLink = destination.priceList ? `/price-list/${destination.priceList}` : '#enquire';
  const priceList = priceLists.find(list => list.slug === destination.priceList);
  // Real rooms only — add-on and supplement tables stay on the full price list.
  const housing = (priceList?.accommodation ?? []).filter(table => !/add-on|supplement/i.test(table.title)).map(table => ({
    ...table,
    from: lowestPrice(table.rows.flatMap(row => row.values))
  }));
  const roomCards: RoomCard[] = housing.length ? housing.map(table => ({
    title: table.title,
    image: table.image ?? fallbackRoomImages[0],
    from: table.from,
    rows: table.rows,
    note: table.note
  })) : roomTypes.map((room, i) => ({
    title: room.name,
    image: fallbackRoomImages[i % fallbackRoomImages.length],
    from: accommodationPrices.find(row => row.room.toLowerCase() === room.name.toLowerCase())?.price ?? '000',
    text: room.text
  }));
  const housingColumns = housing.some(table => table.columns.includes('Shared')) ? ' · Single / Shared occupancy' : housing.length ? '' : undefined;
  return <>
      <DestinationHero destination={destination} season="year-round" headline={copy.h1} lead={copy.lead} primary={{
      label: 'Ask for a free study plan',
      to: '#enquire'
    }} secondary={{
      label: 'See programmes',
      to: '#programmes'
    }} stats={[{
      label: destination.campuses.length > 1 ? 'Campuses' : 'Campus',
      value: String(destination.campuses.length)
    }, {
      label: 'Programmes',
      value: String(available.length)
    }, {
      label: 'Start dates',
      value: 'Every Monday'
    }, {
      label: 'Open',
      value: 'All year'
    }]} data-id="element-1142" data-mp="3sgvqfozowbzx" />

      <Section id="programmes" title="Programmes you can join here" description={`These programmes run all year at our ${destination.name} campus. Each one uses the ES World method and level system.`} data-id="element-1143" data-mp="2j9v5ab4xu68s">
        <div className="space-y-10" data-id="element-1144" data-mp="1qlze1ly516je">
          {courseGroups.map(group => {
          const list = available.filter(course => course.group === group.id);
          if (!list.length) return null;
          return <div key={group.id} data-id="element-1145" data-mp="20hsnuxkh2lxh">
                <h3 className="mb-4 type-label text-muted" data-id="element-1146" data-mp="3g043fs2819wj">{group.label}</h3>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1147" data-mp="3g275xbkk8vi5">
                  {list.map(course => <CourseCard key={course.slug} course={course} data-id="element-1148" data-mp="b3b5b2xsvyt5" />)}
                </div>
              </div>;
        })}
        </div>
        <div className="mt-10" data-id="element-1149" data-mp="f56gcz5j9qk4">
          <ActionLink to={priceLink} data-id="element-1150" data-mp="k3zki0yxk57m">See {destination.name} prices</ActionLink>
        </div>
      </Section>

      <Section id="campus" bg="cream" title="Your campus, ready for you" description="Find the campus address, the facilities you can use and how to travel to class each day." aside={<TextLink to="#housing" data-id="element-1152" data-mp="y2z7l46soc1o">Find a room near campus</TextLink>} data-id="element-1151" data-mp="p2sz5wpye29d">
        <div className="grid gap-10 lg:grid-cols-3" data-id="element-1153" data-mp="12uh6lmp9c2k0">
          <div data-id="element-1154" data-mp="12v0sfei13b3b">
            <MockupTag note="Sample address" className="mb-4" data-id="element-1155" data-mp="12v926xiuu0aa" />
            <div className="space-y-5" data-id="element-1156" data-mp="17sissghzchzp">
              {destination.campuses.map(campus => <address key={campus.name} className="flex gap-3 not-italic" data-id="element-1157" data-mp="1ctq06eg7nf5u">
                  <MapPinIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1158" data-mp="1hr8aa85ys0ff" />
                  <span className="leading-relaxed" data-id="element-1159" data-mp="329iqpt02rcdf">
                    <span className="block type-h5 text-ink" data-id="element-1160" data-mp="32b3pxvxc3jhm">{campus.name}</span>
                    {campus.lines.map(line => <span key={line} className="block" data-id="element-1161" data-mp="2sbmh55mscphf">
                        {line}
                      </span>)}
                  </span>
                </address>)}
            </div>
          </div>
          <div data-id="element-1162" data-mp="3kz8nglnee87z">
            <h3 className="type-h4 text-ink" data-id="element-1163" data-mp="3kzqtg9zv40t3">Facilities</h3>
            <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-1164" data-mp="3g0nn8m85bojj">
              {destination.facilities.map(facility => <li key={facility} className="py-3" data-id="element-1165" data-mp="3b8w3ovneqj2w">
                  {facility}
                </li>)}
            </ul>
          </div>
          <div data-id="element-1166" data-mp="7l3xhx6ooo98">
            <h3 className="type-h4 text-ink" data-id="element-1167" data-mp="7lne9akll6lo">Getting to class</h3>
            <p className="mt-3 flex gap-3 leading-relaxed" data-id="element-1168" data-mp="7n878vr9khhf">
              <TrainFrontIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1169" data-mp="2t428i1k6jiz" />
              {destination.transport}
            </p>
          </div>
        </div>
      </Section>

      <Section id="when" title="Start dates and class times" description="Most English courses start every Monday. Choose a morning, midday or late afternoon timetable." data-id="element-1170" data-mp="220ari25jvcin">
        <TimetableTable data-id="element-1171" data-mp="1sb9e8jqe398d" />
      </Section>

      <Section id="housing" bg="grey" title="A home near your campus" description="We arrange safe, fully serviced student accommodation close to school. Choose a shared or a private space." mockup={housing.length ? undefined : 'Sample prices'} data-id="element-1172" data-mp="1s9y84d9h1pe3">
        <ul className={`grid gap-5 sm:grid-cols-2 ${roomCards.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} data-id="element-1173" data-mp="14fepwpuvanwc">
          {roomCards.map(room => <li key={room.title} className="flex flex-col overflow-hidden rounded-[28px] bg-white" data-id="element-1174" data-mp="2yrd0ow8trruc">
              <div className="relative" data-id="element-1175" data-mp="33rhgsl5tmkk1">
                <img src={room.image} alt={`Sample photo: ${room.title}`} className="aspect-[4/3] w-full object-cover" data-id="element-1176" data-mp="33qfvzoz06jp4" />
                <span className="absolute bottom-3 left-3 rounded-full bg-white px-3 py-1 type-small text-ink" data-id="element-1177" data-mp="s19nbt5e1vsl">
                  from <span className="font-bold" data-id="element-1178" data-mp="11t5lt5bdznxc">{room.from}</span> / week
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5 md:p-6" data-id="element-1179" data-mp="x25qu5ltvfxh">
                <h3 className="type-card-title text-ink" data-id="element-1180" data-mp="x0mc4jftlcxt">{room.title}</h3>
                {room.text ? <p className="mt-2 type-small text-body" data-id="element-1181" data-mp="1as98ej9rilgl">{room.text}</p> : null}
                {room.rows?.length ? <dl className="mt-4 divide-y divide-line rounded-[16px] bg-mist px-4" data-id="element-1182" data-mp="15xdfzi0m5w2y">
                    {room.rows.map(row => <div key={row.label} className="flex items-baseline justify-between gap-3 py-2 type-small" data-id="element-1183" data-mp="1fsx43i8xrqh5">
                        <dt className="text-muted" data-id="element-1184" data-mp="3mkfvza68awoy">{row.label}{/^\d|From/.test(row.label) ? ' weeks' : ''}</dt>
                        <dd className="text-right font-bold text-ink" data-id="element-1185" data-mp="3hm4lcx840i5o">{row.values.join(' / ')}</dd>
                      </div>)}
                  </dl> : null}
                {room.note ? <p className="mt-auto pt-4 type-small text-muted" data-id="element-1186" data-mp="30omnsvgkl78k">{room.note}</p> : null}
              </div>
            </li>)}
        </ul>
        {housingColumns ? <p className="mt-4 type-small text-muted" data-id="element-1187" data-mp="3af8v82e4urg6">Prices per person, per week{housingColumns}. Photos are samples. Subject to availability.</p> : null}

        <div className="mt-10 grid gap-8 rounded-[28px] bg-white p-6 md:p-8 lg:grid-cols-12 lg:items-center" data-id="element-1188" data-mp="2cuv1yl3e07i5">
          <div className="lg:col-span-8" data-id="element-1189" data-mp="2mqpo3q6tkkpo">
            <h3 className="type-h4 text-ink" data-id="element-1190" data-mp="2mpwp5be3qzi3">{priceList?.accommodationIncludes ? 'Good to know' : 'Most homes include'}</h3>
            <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-1191" data-mp="2qtmdbzqv7f2l">
              {(priceList?.accommodationIncludes ?? accommodationFacilities).map(facility => <li key={facility} className="flex items-center gap-3 type-card-desc text-ink" data-id="element-1192" data-mp="6111q1q1van1">
                  <CheckIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1193" data-mp="1qj1rrxv7edet" />
                  {facility}
                </li>)}
            </ul>
          </div>
          <div className="flex flex-col items-start gap-4 lg:col-span-4 lg:items-end" data-id="element-1194" data-mp="1hqzsw40s956n">
            <ActionLink to="#enquire" data-id="element-1195" data-mp="1ct81rc7ypn3i">Request a room near campus</ActionLink>
            {priceList?.accommodation ? <TextLink to={`/price-list/${priceList.slug}#accommodation`} data-id="element-1196" data-mp="17u4xhtzt3kwl">See all prices and add-ons</TextLink> : null}
          </div>
        </div>
      </Section>

      <Section id="activities" title="Weekends with your classmates" description="Practise English outside class on trips and visits with students from other countries." mockup="Sample activities" data-id="element-1197" data-mp="12wmt8gwqhtux">
        <ul className="grid gap-x-10 md:grid-cols-2 lg:grid-cols-3" data-id="element-1198" data-mp="tvx0jm2rsjlf">
          {destination.activities.map(activity => <li key={activity.name} className="border-b border-line py-5" data-id="element-1199" data-mp="2o84uox79owg5">
              <h3 className="type-card-title text-ink" data-id="element-1200" data-mp="2o60r6gda5zge">{activity.name}</h3>
              <p className="mt-2 leading-relaxed" data-id="element-1201" data-mp="iby6ieij4qry">{activity.text}</p>
            </li>)}
        </ul>
      </Section>

      <Section id="faq" bg="cream" title="Plan your arrival with ease" description="Answers to common questions before you travel." data-id="element-1202" data-mp="dgr3za0zxs4m">
        <FaqList items={[{
        q: 'Do I need a visa?',
        a: destination.visaNote
      }, {
        q: 'Can someone meet me at the airport?',
        a: 'Yes. You can book an airport transfer. A driver meets you and takes you to your accommodation.'
      }, {
        q: 'Can I stay with a friend?',
        a: 'Yes. Book a shared room or an apartment together. Tell us your friend\'s name in your enquiry.'
      }]} data-id="element-1203" data-mp="meuruczgf0zl" />
      </Section>

      <EnquirySection title={`Plan your time in ${destination.name}`} description="Tell us your programme, your dates and your questions. Our admissions team replies with a plan and a sample price." submitLabel={`Send my ${destination.name} enquiry`} defaultLocation={destination.slug} data-id="element-1204" data-mp="3na8m1l7o4yu" />
    </>;
}
interface RoomCard {
  title: string;
  image: string;
  from: string;
  text?: string;
  note?: string;
  rows?: {
    label: string;
    values: string[];
  }[];
}

// Sample room photos for locations without real accommodation data yet.
const fallbackRoomImages = ['https://cdn.magicpatterns.com/patterns/generated-images/d3a1516f-b9b3-4325-a446-0e7cc3aef03d.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/46ae61cb-764a-47be-9307-77a775ad4ce6.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/632be5ea-213e-43a8-a5aa-138864f0fe49.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/d516747b-2a00-437a-b43e-d64fcaa6478b.jpg'];

// Picks the cheapest value from strings like "240 USD", keeping its currency.
function lowestPrice(values: string[]) {
  const parsed = values.map(value => ({
    value,
    amount: Number(value.replace(/[^0-9.]/g, ''))
  })).filter(item => item.amount > 0);
  if (!parsed.length) return values[0] ?? '';
  return parsed.reduce((min, item) => item.amount < min.amount ? item : min).value;
}